import React from 'react';

import {
    CreateBasePlanInput,
    CreatePaperPlanMappingsInput,
    ModifyPaperPlanMappingsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IPaperPlanMapping } from 'src/types/IPPRPlanMappings';
import { IToast } from 'src/types/IToaster';
import { getDateAndTimestamp } from 'src/util/dateTimeUtil';

export interface IPaperPlanMappingReviewProps {
    paperPlanMapping: IPaperPlanMapping;
    onCancel: () => void;
    onPrevious: () => void;
    isEditMode: boolean;
}

export const PaperPlanMappingReview = ({
    paperPlanMapping,
    onCancel,
    onPrevious,
    isEditMode
}: IPaperPlanMappingReviewProps) => {
    const { metadata, setToasts, setTopAlertMsg, userInfo } = React.useContext(AppContext);
    const history = useHistory();

    const handleAdd = () => {
        if (paperPlanMapping.basePlanId) {
            addPaperPlanMapping();
        } else {
            addPaperPlanMappingWithNewBasePlan();
        }
    };

    const handleApiSuccess = () => {
        const toast: IToast = isEditMode
            ? {
                  id: 'edit-paper-plan-mapping-toast',
                  timeout: DEFAULT_TOASTER_TIMEOUT,
                  message: 'Changes to mapping have been saved'
              }
            : {
                  id: 'add-paper-plan-mapping-toast',
                  timeout: DEFAULT_TOASTER_TIMEOUT,
                  message: 'New mapping added'
              };
        setToasts([toast]);
        history.push(ROUTES.PAPER_PLAN_MAPPINGS.INDEX);
    };

    const handleApiFail = (apiName: string, message: string) => {
        // eslint-disable-next-line no-console
        console.error(`${apiName} unexpected error`, message);
        setTopAlertMsg(message);
    };

    const addPaperPlanMappingWithNewBasePlan = () => {
        const createBasePlanPromise = contractConfigurationClient
            .createBasePlan(
                CreateBasePlanInput.clone({
                    basePlanName: paperPlanMapping.basePlanName,
                    createdBy: userInfo.username
                })
            )
            .toPromise();

        createBasePlanPromise
            .then(() => {
                return contractConfigurationClient
                    .createPaperPlanMappings(CreatePaperPlanMappingsInput.clone(mapPaperPlanMappingToAddInput()))
                    .toPromise();
            })
            .then(() => {
                handleApiSuccess();
            })
            .catch((error: UnknownCoralError) => {
                handleApiFail('createBasePlan or createPaperPlanMappings', error.receivedException?.message || '');
            });
    };

    const addPaperPlanMapping = () => {
        contractConfigurationClient
            .createPaperPlanMappings(CreatePaperPlanMappingsInput.clone(mapPaperPlanMappingToAddInput()))
            .subscribe(
                () => {
                    handleApiSuccess();
                },
                (error: UnknownCoralError) => {
                    handleApiFail('createPaperPlanMappings', error.receivedException?.message || '');
                }
            );
    };

    const editPaperPlanMapping = () => {
        contractConfigurationClient
            .modifyPaperPlanMappings(ModifyPaperPlanMappingsInput.clone(mapPaperPlanMappingtoEditInput()))
            .subscribe(
                () => {
                    handleApiSuccess();
                },
                (error: UnknownCoralError) => {
                    handleApiFail('modifyPaperPlanMappings', error.receivedException?.message || '');
                }
            );
    };

    const mapPaperPlanMappingToAddInput = () => {
        return {
            basePlanName: paperPlanMapping.basePlanName,
            basePromotion: paperPlanMapping.basePlanPromoId,
            createdBy: userInfo.username,
            description: paperPlanMapping.description,
            endDate: paperPlanMapping.endDate ? getDateAndTimestamp(paperPlanMapping.endDate)[0] : undefined,
            paperPlanName: paperPlanMapping.paperPlanName,
            startDate: paperPlanMapping.startDate ? getDateAndTimestamp(paperPlanMapping.startDate)[0] : undefined,
            territories: paperPlanMapping.territoryIds
        } as CreatePaperPlanMappingsInput;
    };

    const mapPaperPlanMappingtoEditInput = () => {
        return {
            changedBy: userInfo.username,
            paperPlanMappingId: parseInt(paperPlanMapping.paperPlanMappingId),
            updatedBasePlanName: paperPlanMapping.basePlanName,
            updatedBasePromotion: paperPlanMapping.basePlanPromoId,
            updatedDescription: paperPlanMapping.description,
            updatedEndDate: paperPlanMapping.endDate ? getDateAndTimestamp(paperPlanMapping.endDate)[0] : undefined,
            updatedPaperPlanName: paperPlanMapping.paperPlanName,
            updatedStartDate: paperPlanMapping.startDate
                ? getDateAndTimestamp(paperPlanMapping.startDate)[0]
                : undefined,
            updatedTerritory: paperPlanMapping.territoryId
        } as ModifyPaperPlanMappingsInput;
    };

    return (
        <>
            <Heading level={1}>{isEditMode ? 'Review and save' : 'Review and add'}</Heading>
            <Column spacingInset="450 none 500 none" spacing="450">
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        PAPER plan ID
                    </Text>
                    <Text>{paperPlanMapping.paperPlanName}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Base plan ID
                    </Text>
                    <Text>{paperPlanMapping.basePlanName}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Base plan promo ID
                    </Text>
                    <Text>{paperPlanMapping.basePlanPromoId || '--'}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Territory
                    </Text>
                    {isEditMode ? (
                        <TerritoryFlag
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            territoryCode={metadata.territoryMapping[paperPlanMapping.territoryId!].territoryCode!}
                            showTerritoryCode={false}
                            showTerritoryName={true}
                        />
                    ) : (
                        paperPlanMapping.territoryIds.map((territoryId) => (
                            <TerritoryFlag
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                territoryCode={metadata.territoryMapping[territoryId].territoryCode!}
                                showTerritoryCode={false}
                                showTerritoryName={true}
                                key={territoryId}
                            />
                        ))
                    )}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Start date
                    </Text>
                    <Text>{paperPlanMapping.startDate}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        End date
                    </Text>
                    <Text>{paperPlanMapping.endDate || '--'}</Text>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="h100">
                        Description
                    </Text>
                    <Text>{paperPlanMapping.description}</Text>
                </Column>
            </Column>
            <Row alignmentHorizontal="end">
                <Button type="link" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="tertiary" onClick={onPrevious}>
                    Previous
                </Button>
                <Button type="primary" onClick={isEditMode ? editPaperPlanMapping : handleAdd}>
                    {isEditMode ? 'Save changes' : 'Add mapping'}
                </Button>
            </Row>
        </>
    );
};
